import React, { useRef, useState } from 'react'
import { Form, Input } from 'semantic-ui-react'
import styled from 'styled-components'
import * as R from 'ramda'
import SortableList from '../SortableList'
import Popup from 'shared/components/Popup'
import { LinkButton } from 'elements/LinkButton'
import {
  TEMP_ID_PREFIX,
  getAutoId,
  MAX_CHOICES_PER_QUESTION,
} from 'organisms/SurveySettingsV2/constants'

const TOOLTIP_MAX_CHOICES = `Maximum of ${MAX_CHOICES_PER_QUESTION} choices reached. Remove one to add another.`

export default function ChoiceQuestionInput({
  choices,
  deletedExistChoices,
  arrayHelpers,
  handleFieldChange,
}) {
  // Capture originalChoiceCount ONCE at mount — never live from current length —
  // so a grandfathered survey provisioned beyond the cap can shrink and re-grow
  // back up to its original count without being force-truncated.
  const originalChoiceCountRef = useRef(choices.length)
  const [choicesState, setChoicesState] = useState(choices)

  // cap = max(10, originalChoiceCount) — grandfathered surveys keep their original count.
  const cap = Math.max(MAX_CHOICES_PER_QUESTION, originalChoiceCountRef.current)

  const hasReachedMaxChoices = () => {
    return choicesState.length >= cap
  }

  const addChoice = () => {
    if (hasReachedMaxChoices()) {
      return
    }

    const newChoice = {
      id: `${TEMP_ID_PREFIX}${getAutoId()}`,
      label: null,
      value: null,
      sentiment: null,
    }
    setChoicesState((previousChoices) => {
      return previousChoices.concat(newChoice)
    })
    arrayHelpers.push(newChoice)
  }

  const removeChoice = (index) => {
    const choice = choicesState[index]
    const choiceIdExisted = R.prop('id', choice) && !`${choice.id}`.startsWith(TEMP_ID_PREFIX)
    if (choiceIdExisted) {
      handleFieldChange(`deletedExistChoices`, () => [
        ...deletedExistChoices,
        choicesState[index],
      ])()
    }

    setChoicesState((previousChoices) => {
      return R.remove(index, 1, previousChoices)
    })
    arrayHelpers.remove(index)
  }

  const changeChoiceLabel = (index, value) => {
    setChoicesState((previousChoices) => {
      return R.assocPath(
        [index],
        { ...previousChoices[index], value: value, label: value },
        previousChoices
      )
    })
    handleFieldChange(`choices.${index}.label`, () => value)()
    handleFieldChange(`choices.${index}.value`, () => value)()
  }

  const changeChoiceOrder = (choices) => {
    setChoicesState(choices)
    handleFieldChange(`choices`, () => choices)()
  }

  const isAtCap = hasReachedMaxChoices()

  return (
    <Form.Field>
      <label>Choices</label>
      <SortableList
        items={choicesState}
        onChange={changeChoiceOrder}
        renderItem={(choice, index) => (
          <SortableList.Item id={choice.id}>
            <Choice className="relative flex items-center justify-center w-full" key={choice.id}>
              <Input
                required
                maxLength={250}
                className="flex-1 large"
                data-testid="choice-input"
                value={choice.label}
                onChange={(e) => {
                  changeChoiceLabel(index, e.target.value)
                }}
              />
              <SortableList.DragHandle />

              <RemoveChoiceButton
                onRemove={() => removeChoice(index)}
                canRemoveChoice={index > 1}
              />
            </Choice>
          </SortableList.Item>
        )}
      />

      {isAtCap ? (
        <Popup basic tooltip={TOOLTIP_MAX_CHOICES}>
          <LinkButton
            type="button"
            disabled={true}
            aria-disabled="true"
            className="self-start"
            data-testid="add-choice-btn"
          >
            <i className="icon far fa-plus" />
            Add choice
          </LinkButton>
        </Popup>
      ) : (
        <LinkButton
          type="button"
          onClick={addChoice}
          className="self-start"
          data-testid="add-choice-btn"
        >
          <i className="icon far fa-plus" />
          Add choice
        </LinkButton>
      )}
    </Form.Field>
  )
}

function RemoveChoiceButton({ onRemove, canRemoveChoice }) {
  if (canRemoveChoice) {
    return (
      <i
        className="ml-10 icon fas fa-xmark text-grey link"
        onClick={onRemove}
        data-testid="remove-choice-btn"
      />
    )
  } else {
    return <i className="ml-10 text-transparent icon fas fa-xmark link" />
  }
}

const Choice = styled.div`
  &:not(:first-child) {
    margin-top: 0.5rem;
  }
`



// WEBPACK FOOTER //
// ./src/components/ChoiceQuestionInput/index.tsx